import {
  AcademicCapIcon,
  AdjustmentsIcon,
  BookOpenIcon,
  ChatAlt2Icon,
  ChevronLeftIcon,
  EmojiHappyIcon,
  FilmIcon,
  GlobeAltIcon,
  HashtagIcon,
  HeartIcon,
  SearchIcon,
  SparklesIcon,
  SpeakerphoneIcon,
  XIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Voice } from 'types';

import clsxm from '@/lib/clsxm';
import {
  activeVoiceFilterCount,
  buildVoiceSearchText,
  countVoiceFacets,
  EMPTY_VOICE_FILTERS,
  filterVoices,
  sortFavoritesFirst,
  VoiceFacet,
  VoiceFilterContext,
  voiceFilterLanguages,
  VoiceFilterState,
  voiceLanguages,
} from '@/lib/voiceFilters';

import Toggle from '@/components/atoms/Toggle';
import ChipGroup, {
  ChipOption,
} from '@/components/speech/voicePicker/ChipGroup';
import FilterMenu, {
  FilterMenuOption,
} from '@/components/speech/voicePicker/FilterMenu';
import {
  FemaleIcon,
  MaleIcon,
} from '@/components/speech/voicePicker/GenderIcons';
import VoiceAvatar from '@/components/speech/voicePicker/VoiceAvatar';
import VoiceRow, {
  VoiceBadge,
  VoiceLanguageInfo,
} from '@/components/speech/voicePicker/VoiceRow';
import { languages as languageFlags } from '@/components/TextToSpeechLanguages';

import { CLASSIC_VOICE_MODEL } from '@/constants/elevenLabsModels';
import { isMiniMaxVoiceModel } from '@/constants/minimaxVoice';
import {
  ageLabel,
  genderLabel,
  toneLabel,
  VOICE_AGES,
  VOICE_GENDERS,
  VOICE_USE_CASES,
  VoiceUseCase,
  voiceUseCaseLabel,
  voiceUseCaseShortLabel,
} from '@/constants/voiceAttributes';
import axiosInstance from '@/utils/httpClient';
import { useUser } from '@/utils/useUser';

const flagMap = new Map<string, string>();
for (const language of languageFlags) {
  // The first entry for a name wins.
  if (!flagMap.has(language.name)) {
    flagMap.set(language.name, language.flag);
  }
}

const getFlag = (name: string): string => flagMap.get(name) ?? '';

// Voices that read audio tags at every speed: the curated expressive voices
// and designed voices (pinned to Eleven v3). Other ElevenLabs voices read
// tags on Eleven v4 only, i.e. at 1.0x speed.
const isExpressiveVoice = (voice: Voice) =>
  Boolean(voice.expressive) || voice.model === 'eleven_v3';

/** The user's own voices (cloned, designed or professional). */
const isOwnVoice = (voice: Voice) => Boolean(voice.user_id);

// Professional (MiniMax) clones can't be deleted: each one costs $1.50.
const isDeletableVoice = (voice: Voice) =>
  isOwnVoice(voice) && !isMiniMaxVoiceModel(voice.model);

// How many rows to render per "page". The list holds ~2,000 voices; mounting
// them all at once (each with an <Image>) is slow, so more render on scroll.
const PAGE_SIZE = 60;

// A gender / age / use case / tone filter is offered once at least this
// share of the voices carries the label (ElevenLabs labels ~98% of the
// catalog; voices served before the catalog table is seeded carry none).
const MIN_FACET_COVERAGE = 0.5;

// Tones are many (30+): the most common show first, the rest behind "Show all".
const TONES_COLLAPSED = 10;

const USE_CASE_ICONS: Record<VoiceUseCase, typeof ChatAlt2Icon> = {
  conversational: ChatAlt2Icon,
  narrative_story: BookOpenIcon,
  informative_educational: AcademicCapIcon,
  social_media: HashtagIcon,
  characters_animation: EmojiHappyIcon,
  entertainment_tv: FilmIcon,
  advertisement: SpeakerphoneIcon,
};

/**
 * Size and corners of the dialog that hosts the picker, for every page that
 * opens it (ElevenLabs' voice picker: min(1219px, 95vw) wide, 24px corners).
 */
export const VOICE_PICKER_PANEL_CLASS =
  'rounded-3xl sm:w-[95vw] sm:max-w-[1220px]';

type Scope = 'all' | 'mine' | 'favorites';

type AttributeFacet = Extract<
  VoiceFacet,
  'gender' | 'age' | 'useCase' | 'tone'
>;

const attributeValue = (voice: Voice, facet: AttributeFacet) =>
  facet === 'useCase' ? voice.use_case : voice[facet];

const readFavouriteVoices = (): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const stored = JSON.parse(localStorage.getItem('favouriteVoices') || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

const dedupeVoices = (voices: Voice[]) => {
  const seen = new Set<string>();
  return voices.filter((voice) => {
    if (seen.has(voice.voice_id)) return false;
    seen.add(voice.voice_id);
    return true;
  });
};

interface VoiceSelectionProps {
  voices: Voice[];
  setVoice: (voiceName: string) => void;
  setVoiceID: (voiceID: string) => void;
  currentVoice: string | null;
  /** The selected voice's id. Voices can share a name, so pass it when known. */
  currentVoiceId?: string | null;
  /**
   * Clears the selection. Cancel calls it when the picker opened with no
   * voice selected (Motion Control's voice change starts empty), since
   * picking a voice applies it right away.
   */
  onClearSelection?: () => void;
  closeModal: () => void;
  initialLanguage?: string;
  initialAccent?: string;
  onLanguageChange?: (language: string) => void;
  onAccentChange?: (accent: string) => void;
  /** Pre-apply the "Audio tags" (v3) filter, e.g. when opened from the tag warning. */
  initialOnlyV3?: boolean;
  /** Show placeholder rows while the voices are being fetched. */
  isLoading?: boolean;
}

const FilterSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section>
    <h3 className='mb-2.5 text-[13px] font-semibold text-gray-900 dark:text-slate-100'>
      {title}
    </h3>
    {children}
  </section>
);

const VoiceSelection: React.FC<VoiceSelectionProps> = ({
  voices,
  setVoice,
  setVoiceID,
  currentVoice,
  currentVoiceId = null,
  onClearSelection,
  closeModal,
  initialLanguage = '',
  initialAccent = '',
  onLanguageChange,
  onAccentChange,
  initialOnlyV3 = false,
  isLoading = false,
}) => {
  const { t: translateKey, lang } = useLocale('voice-picker');
  // Language, accent and label names repeat across ~2,000 voices, and in
  // development next-translate logs a warning for every missing key (most of
  // these names have no translation yet): look each string up only once.
  const t = useMemo(() => {
    const cache = new Map<string, string>();
    return (key: string): string => {
      const cached = cache.get(key);
      if (cached !== undefined) return cached;
      const value = translateKey<string>(key);
      cache.set(key, value);
      return value;
    };
  }, [translateKey]);
  const { isFreePlan } = useUser();
  const [localVoices, setLocalVoices] = useState<Voice[]>(() =>
    dedupeVoices(voices),
  );
  const [filters, setFilters] = useState<VoiceFilterState>({
    ...EMPTY_VOICE_FILTERS,
    language: initialLanguage,
    accent: initialAccent,
    onlyExpressive: initialOnlyV3,
  });
  const [scope, setScope] = useState<Scope>('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showAllTones, setShowAllTones] = useState(false);
  // Read once on mount (the picker only ever renders in the browser, after a
  // click); setting it from an effect would filter the whole list twice.
  const [favouriteVoices, setFavouriteVoices] =
    useState<string[]>(readFavouriteVoices);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  // Cancel puts back the voice that was selected when the picker opened
  // (picking a voice applies it right away).
  const initialSelection = useRef({ name: currentVoice, id: currentVoiceId });

  const updateFilters = (patch: Partial<VoiceFilterState>) =>
    setFilters((current) => ({ ...current, ...patch }));

  // A new list from the parent (e.g. after cloning a voice) replaces the local
  // copy. Skips the first run: the state already starts from `voices`, and a
  // fresh copy would filter and count the whole list a second time on open.
  const voicesSource = useRef(voices);
  useEffect(() => {
    if (voicesSource.current === voices) return;
    voicesSource.current = voices;
    setLocalVoices(dedupeVoices(voices));
  }, [voices]);

  // The audio-tag warning opens the picker with the v3 filter on; a normal
  // "Change voice" opens it off. Keeps the same object when nothing changes,
  // so opening the picker doesn't re-filter the list.
  useEffect(() => {
    setFilters((current) =>
      current.onlyExpressive === initialOnlyV3
        ? current
        : { ...current, onlyExpressive: initialOnlyV3 },
    );
  }, [initialOnlyV3]);

  // Search is the first thing to reach for on a desktop; on a phone, focusing
  // it would open the keyboard over the list.
  useEffect(() => {
    if (!window.matchMedia?.('(min-width: 768px)').matches) return;
    const timer = window.setTimeout(() => searchRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    return () => audio?.pause();
  }, []);

  const favouriteSet = useMemo(
    () => new Set(favouriteVoices),
    [favouriteVoices],
  );

  const ownCount = useMemo(
    () => localVoices.filter(isOwnVoice).length,
    [localVoices],
  );
  const favouriteCount = useMemo(
    () =>
      localVoices.filter((voice) => favouriteSet.has(voice.voice_id)).length,
    [localVoices, favouriteSet],
  );

  // Deleting the last own voice empties "My voices": go back to everything.
  useEffect(() => {
    if (scope === 'mine' && ownCount === 0) setScope('all');
  }, [scope, ownCount]);

  // What each voice can be found by: its name, description, language and
  // accent, plus its gender / age / use case / tone in English and in the
  // user's language ("mujer" finds female voices in Spanish). Built per voice
  // on first use, i.e. once someone searches, not when the picker opens.
  const searchText = useMemo(() => {
    const cache = new WeakMap<Voice, string>();
    return (voice: Voice): string => {
      let text = cache.get(voice);
      if (text === undefined) {
        const labels = [
          genderLabel(voice.gender),
          ageLabel(voice.age),
          voiceUseCaseLabel(voice.use_case),
          voiceUseCaseShortLabel(voice.use_case),
          toneLabel(voice.tone),
        ].filter(Boolean) as string[];
        text = buildVoiceSearchText(voice, [
          ...labels,
          ...labels.map(t),
          ...voiceLanguages(voice).map(t),
          voice.accent ? t(voice.accent) : null,
        ]);
        cache.set(voice, text);
      }
      return text;
    };
  }, [t]);

  const filterContext: VoiceFilterContext = useMemo(
    () => ({
      favorites: favouriteSet,
      isExpressive: isExpressiveVoice,
      searchText,
    }),
    [favouriteSet, searchText],
  );

  const scopedVoices = useMemo(() => {
    if (scope === 'mine') return localVoices.filter(isOwnVoice);
    if (scope === 'favorites') {
      return localVoices.filter((voice) => favouriteSet.has(voice.voice_id));
    }
    return localVoices;
  }, [scope, localVoices, favouriteSet]);

  const filteredVoices = useMemo(
    () => filterVoices(scopedVoices, filters, filterContext),
    [scopedVoices, filters, filterContext],
  );

  // How many voices each option would show, given the other filters.
  const facetCounts = useMemo(
    () => countVoiceFacets(scopedVoices, filters, filterContext),
    [scopedVoices, filters, filterContext],
  );

  const activeFilters = activeVoiceFilterCount(filters);
  const hasSearch = Boolean(filters.search.trim());

  const formatCount = (count: number) => count.toLocaleString(lang);

  const allLanguages = useMemo(
    () => Array.from(new Set(localVoices.flatMap(voiceFilterLanguages))),
    [localVoices],
  );

  const languageOptions: FilterMenuOption[] = useMemo(
    () =>
      allLanguages
        .map((language) => {
          const count = facetCounts.language.get(language) || 0;
          return {
            value: language,
            label: t(language),
            flag: getFlag(language),
            count: count.toLocaleString(lang),
            disabled: count === 0,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label, lang)),
    [allLanguages, facetCounts, t, lang],
  );

  // Accents are language-specific, so only those the other filters allow.
  const accentOptions: FilterMenuOption[] = useMemo(() => {
    const accents = Array.from(facetCounts.accent.keys());
    if (filters.accent && !accents.includes(filters.accent)) {
      accents.push(filters.accent);
    }
    return accents
      .map((accent) => ({
        value: accent,
        label: t(accent),
        flag: getFlag(accent),
        count: (facetCounts.accent.get(accent) || 0).toLocaleString(lang),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, lang));
  }, [facetCounts, filters.accent, t, lang]);

  // Only offer the attribute filters the catalog has data for, with the
  // values it actually carries.
  const facetValues = useMemo(() => {
    const total = localVoices.length || 1;
    const result: Partial<Record<AttributeFacet, string[]>> = {};
    const facets: AttributeFacet[] = ['gender', 'age', 'useCase', 'tone'];
    for (const facet of facets) {
      const frequency = new Map<string, number>();
      for (const voice of localVoices) {
        const value = attributeValue(voice, facet);
        if (value) frequency.set(value, (frequency.get(value) || 0) + 1);
      }
      const labelled = Array.from(frequency.values()).reduce(
        (sum, count) => sum + count,
        0,
      );
      if (labelled / total < MIN_FACET_COVERAGE) continue;
      const known: readonly string[] | null =
        facet === 'gender'
          ? VOICE_GENDERS
          : facet === 'age'
            ? VOICE_AGES
            : facet === 'useCase'
              ? VOICE_USE_CASES
              : null;
      result[facet] = known
        ? known.filter((value) => frequency.has(value))
        : // Tones: the most common first.
          Array.from(frequency.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([value]) => value);
    }
    return result;
  }, [localVoices]);

  const chipOptions = (
    facet: AttributeFacet,
    display: (value: string) => string | null,
    icon?: (value: string) => React.ReactNode,
  ): ChipOption[] =>
    (facetValues[facet] || []).map((value) => ({
      value,
      label: t(display(value) || value),
      icon: icon?.(value),
      disabled: !facetCounts[facet].get(value),
    }));

  const genderChips = chipOptions('gender', genderLabel, (value) =>
    value === 'female' ? (
      <FemaleIcon className='h-3.5 w-3.5' />
    ) : value === 'male' ? (
      <MaleIcon className='h-3.5 w-3.5' />
    ) : null,
  );
  const ageChips = chipOptions('age', ageLabel);
  const useCaseChips = chipOptions(
    'useCase',
    voiceUseCaseShortLabel,
    (value) => {
      const Icon = USE_CASE_ICONS[value as VoiceUseCase];
      return Icon ? <Icon className='h-3.5 w-3.5' /> : null;
    },
  );
  const allToneChips = chipOptions('tone', toneLabel);
  const toneChips = showAllTones
    ? allToneChips
    : allToneChips.filter(
        (chip, index) => index < TONES_COLLAPSED || chip.value === filters.tone,
      );

  const hasV3Voices = useMemo(
    () => localVoices.some(isExpressiveVoice),
    [localVoices],
  );

  const setLanguage = (value: string) => {
    // Accents belong to a language: a stale accent (e.g. "Singaporean")
    // would filter the new language down to nothing.
    updateFilters({ language: value, accent: '' });
    onLanguageChange?.(value);
    if (filters.accent) onAccentChange?.('');
  };

  const setAccent = (value: string) => {
    updateFilters({ accent: value });
    onAccentChange?.(value);
  };

  const clearFilters = () => {
    setFilters(EMPTY_VOICE_FILTERS);
    if (filters.language) onLanguageChange?.('');
    if (filters.accent) onAccentChange?.('');
  };

  const activeChips = [
    filters.language && {
      key: 'language',
      label: `${getFlag(filters.language)} ${t(filters.language)}`.trim(),
      clear: () => setLanguage(''),
    },
    filters.accent && {
      key: 'accent',
      label: `${getFlag(filters.accent)} ${t(filters.accent)}`.trim(),
      clear: () => setAccent(''),
    },
    filters.useCase && {
      key: 'useCase',
      label: t(voiceUseCaseShortLabel(filters.useCase) || filters.useCase),
      clear: () => updateFilters({ useCase: '' }),
    },
    filters.gender && {
      key: 'gender',
      label: t(genderLabel(filters.gender) || filters.gender),
      clear: () => updateFilters({ gender: '' }),
    },
    filters.age && {
      key: 'age',
      label: t(ageLabel(filters.age) || filters.age),
      clear: () => updateFilters({ age: '' }),
    },
    filters.tone && {
      key: 'tone',
      label: t(toneLabel(filters.tone) || filters.tone),
      clear: () => updateFilters({ tone: '' }),
    },
    filters.onlyExpressive && {
      key: 'expressive',
      label: t('Audio tags'),
      clear: () => updateFilters({ onlyExpressive: false }),
    },
  ].filter(Boolean) as Array<{ key: string; label: string; clear: () => void }>;

  // With nothing filtered, "All voices" reads as a library: favourites, then
  // the user's own voices, then the catalog. Otherwise one ranked list.
  const grouped = scope === 'all' && !hasSearch && activeFilters === 0;
  const sections = useMemo(() => {
    if (!grouped) {
      return [
        {
          key: 'results',
          title: '',
          voices: sortFavoritesFirst(
            filteredVoices,
            favouriteSet,
            filters.language,
          ),
        },
      ];
    }
    const favourites: Voice[] = [];
    const own: Voice[] = [];
    const library: Voice[] = [];
    for (const voice of filteredVoices) {
      if (favouriteSet.has(voice.voice_id)) favourites.push(voice);
      else if (isOwnVoice(voice)) own.push(voice);
      else library.push(voice);
    }
    return [
      { key: 'favorites', title: t('Favorites'), voices: favourites },
      { key: 'mine', title: t('My voices'), voices: own },
      { key: 'library', title: t('Voice library'), voices: library },
    ].filter((section) => section.voices.length > 0);
  }, [grouped, filteredVoices, favouriteSet, filters.language, t]);

  // Only render the first `visibleCount` voices; more load as the user scrolls.
  const visibleSections = useMemo(() => {
    let budget = visibleCount;
    const visible: typeof sections = [];
    for (const section of sections) {
      if (budget <= 0) break;
      visible.push({ ...section, voices: section.voices.slice(0, budget) });
      budget -= section.voices.length;
    }
    return visible;
  }, [sections, visibleCount]);

  // Back to the top whenever the result set changes.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    listRef.current?.scrollTo?.({ top: 0 });
  }, [filters, scope]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    if (visibleCount >= filteredVoices.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((count) =>
            Math.min(count + PAGE_SIZE, filteredVoices.length),
          );
        }
      },
      { root: listRef.current, rootMargin: '160px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleCount, filteredVoices.length]);

  const selectedVoice = useMemo(
    () =>
      (currentVoiceId &&
        localVoices.find((voice) => voice.voice_id === currentVoiceId)) ||
      localVoices.find((voice) => voice.name === currentVoice) ||
      null,
    [localVoices, currentVoice, currentVoiceId],
  );
  const selectedId = selectedVoice?.voice_id ?? currentVoiceId;

  const isLocked = (voice: Voice) => voice.name === 'Adam' && isFreePlan;

  // Rows keep room for buttons other rows have, so the language column lines
  // up. Taken from the whole result list (not just the rendered rows), so
  // loading more rows on scroll never shifts it.
  const reserveDeleteSlot = useMemo(
    () => filteredVoices.some(isDeletableVoice),
    [filteredVoices],
  );
  const reserveUpgradeSlot = useMemo(
    () => isFreePlan && filteredVoices.some((voice) => voice.name === 'Adam'),
    [filteredVoices, isFreePlan],
  );

  const selectVoice = (voice: Voice) => {
    if (isLocked(voice)) return;
    setVoice(voice.name);
    setVoiceID(voice.voice_id);
  };

  const chooseVoice = (voice: Voice) => {
    if (isLocked(voice)) return;
    selectVoice(voice);
    closeModal();
  };

  const cancel = () => {
    const initial = initialSelection.current;
    const changed = initial.id
      ? initial.id !== selectedId
      : initial.name !== currentVoice;
    if (changed && initial.name) {
      const id =
        initial.id ||
        localVoices.find((voice) => voice.name === initial.name)?.voice_id;
      if (id) {
        setVoice(initial.name);
        setVoiceID(id);
      }
    } else if (changed && !initial.id) {
      // Opened with nothing selected: back to nothing.
      onClearSelection?.();
    }
    closeModal();
  };

  const togglePlay = (voice: Voice) => {
    const audio = audioRef.current;
    if (!audio || !voice.preview_url) return;
    if (playingId === voice.voice_id) {
      audio.pause();
      setPlayingId(null);
      return;
    }
    audio.src = voice.preview_url;
    audio.play().catch(() => setPlayingId(null));
    setPlayingId(voice.voice_id);
    // Previewing a voice also picks it.
    selectVoice(voice);
  };

  const toggleFavourite = (voiceId: string) => {
    const updated = favouriteSet.has(voiceId)
      ? favouriteVoices.filter((id) => id !== voiceId)
      : [...favouriteVoices, voiceId];
    try {
      localStorage.setItem('favouriteVoices', JSON.stringify(updated));
    } catch {
      // Private mode: keep the favourite for this session only.
    }
    setFavouriteVoices(updated);
  };

  const handleDelete = (voice: Voice) => {
    if (!window.confirm(t('Are you sure you want to delete this voice?'))) {
      return;
    }
    if (playingId === voice.voice_id) {
      audioRef.current?.pause();
      setPlayingId(null);
    }
    setLocalVoices((current) =>
      current.filter((item) => item.voice_id !== voice.voice_id),
    );
    axiosInstance.post('/api/delete-voice', { voice_id: voice.voice_id });
  };

  const voiceBadges = (voice: Voice): VoiceBadge[] => {
    const badges: VoiceBadge[] = [];
    if (isExpressiveVoice(voice)) {
      badges.push({
        key: 'tags',
        label: t('Audio tags'),
        tone: 'primary',
        icon: <SparklesIcon className='h-3 w-3' />,
      });
    }
    if (voice.isOpenAI) {
      badges.push({ key: 'saver', label: t('Saver'), tone: 'amber' });
    }
    if (isOwnVoice(voice)) {
      badges.push({
        key: 'own',
        label: isMiniMaxVoiceModel(voice.model)
          ? t('Professional')
          : voice.model === 'eleven_v3'
            ? t('Designed')
            : t('Cloned'),
        tone: 'gray',
      });
      if (voice.model === CLASSIC_VOICE_MODEL) {
        badges.push({ key: 'classic', label: t('Classic'), tone: 'gray' });
      }
    }
    return badges;
  };

  // "Female · Young · Narration · Calm"; the user's own voices carry
  // no labels, so their description is shown instead.
  const voiceMeta = (voice: Voice) => {
    const labels = [
      genderLabel(voice.gender),
      ageLabel(voice.age),
      voiceUseCaseShortLabel(voice.use_case),
      toneLabel(voice.tone),
    ].filter(Boolean) as string[];
    if (labels.length) return labels.map((label) => t(label)).join(' · ');
    // Some own voices were saved with a bare "''" as their description.
    const description = voice.description?.trim() || '';
    return /[\p{L}\p{N}]/u.test(description) ? description : '';
  };

  const voiceLanguage = (voice: Voice): VoiceLanguageInfo | null => {
    const languages = voiceLanguages(voice);
    if (!languages.length) return null;
    const primary = languages[0];
    const accent =
      voice.accent && voice.accent.toLowerCase() !== primary.toLowerCase()
        ? voice.accent
        : undefined;
    return {
      flag: (accent && getFlag(accent)) || getFlag(primary),
      label: t(primary),
      accent: accent ? t(accent) : undefined,
      // Its other listed languages plus those ElevenLabs verified it for.
      others: Array.from(
        new Set([...languages.slice(1), ...(voice.other_languages || [])]),
      )
        .filter((name) => name !== primary)
        .map(t)
        .sort((a, b) => a.localeCompare(b, lang)),
    };
  };

  const rowLabels = {
    play: t('Play'),
    pause: t('Pause'),
    use: t('Use'),
    selected: t('Selected'),
    upgrade: t('Upgrade'),
    favorite: t('Add to favorites'),
    unfavorite: t('Remove from favorites'),
    remove: t('Delete voice'),
  };

  const tabs: Array<{ key: Scope; label: string; count?: number }> = [
    { key: 'all', label: t('All voices') },
    ...(ownCount > 0
      ? [{ key: 'mine' as const, label: t('My voices'), count: ownCount }]
      : []),
    { key: 'favorites', label: t('Favorites'), count: favouriteCount },
  ];

  const resultsLabel = `${formatCount(filteredVoices.length)} ${t(
    filteredVoices.length === 1 ? 'voice' : 'voices',
  )}`;

  const filterPanel = (
    <div className='space-y-7'>
      <FilterSection title={t('Language')}>
        <div className='space-y-2'>
          <FilterMenu
            value={filters.language}
            placeholder={t('All languages')}
            options={languageOptions}
            onChange={setLanguage}
            searchPlaceholder={t('Search languages')}
            noMatchesLabel={t('No matches')}
            clearLabel={t('Clear filters')}
            icon={<GlobeAltIcon className='h-4 w-4 text-gray-400' />}
          />
          {accentOptions.length > 0 && (
            <FilterMenu
              value={filters.accent}
              placeholder={t('All accents')}
              options={accentOptions}
              onChange={setAccent}
              searchPlaceholder={
                accentOptions.length > 8 ? t('Search accents') : undefined
              }
              noMatchesLabel={t('No matches')}
              clearLabel={t('Clear filters')}
              icon={<span className='text-xs text-gray-400'>Aa</span>}
            />
          )}
        </div>
      </FilterSection>
      {useCaseChips.length > 0 && (
        <FilterSection title={t('Use case')}>
          <ChipGroup
            options={useCaseChips}
            value={filters.useCase}
            onChange={(value) => updateFilters({ useCase: value })}
          />
        </FilterSection>
      )}
      {genderChips.length > 0 && (
        <FilterSection title={t('Gender')}>
          <ChipGroup
            options={genderChips}
            value={filters.gender}
            onChange={(value) => updateFilters({ gender: value })}
          />
        </FilterSection>
      )}
      {ageChips.length > 0 && (
        <FilterSection title={t('Age')}>
          <ChipGroup
            options={ageChips}
            value={filters.age}
            onChange={(value) => updateFilters({ age: value })}
          />
        </FilterSection>
      )}
      {toneChips.length > 0 && (
        <FilterSection title={t('Tone')}>
          <ChipGroup
            options={toneChips}
            value={filters.tone}
            onChange={(value) => updateFilters({ tone: value })}
          />
          {allToneChips.length > TONES_COLLAPSED && (
            <button
              type='button'
              onClick={() => setShowAllTones((value) => !value)}
              className='mt-2.5 text-xs font-medium text-gray-500 underline-offset-4 hover:text-gray-900 hover:underline dark:text-slate-400 dark:hover:text-white'
            >
              {showAllTones ? t('Show less') : t('Show all')}
            </button>
          )}
        </FilterSection>
      )}
      {hasV3Voices && (
        <div className='flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-white/5'>
          <SparklesIcon className='mt-0.5 h-4 w-4 shrink-0 text-primary-500' />
          <div className='min-w-0 flex-1'>
            <p className='text-[13px] font-medium text-gray-900 dark:text-slate-100'>
              {t('Audio tags')}
            </p>
            <p className='mt-0.5 text-xs leading-snug text-gray-500 dark:text-slate-400'>
              {t('Voices that act out tags like [laughs] and [whispers]')}
            </p>
          </div>
          <Toggle
            isSmall
            initialValue={filters.onlyExpressive}
            onChange={(value) => updateFilters({ onlyExpressive: value })}
          />
        </div>
      )}
    </div>
  );

  const noFilters = activeFilters === 0 && !hasSearch;
  const emptyFavourites = scope === 'favorites' && noFilters;

  return (
    <div className='relative flex h-[min(48.25rem,90vh)] w-full overflow-hidden text-left'>
      <aside className='hidden w-72 shrink-0 overflow-y-auto border-r border-gray-100 bg-gray-50/70 px-5 pb-6 pt-5 dark:border-white/5 dark:bg-slate-950/40 md:block'>
        {filterPanel}
      </aside>

      <section className='flex min-w-0 flex-1 flex-col'>
        <div className='flex flex-col gap-2 border-b border-gray-100 px-4 py-3 dark:border-white/5 sm:flex-row sm:items-center sm:gap-3 sm:pl-5 sm:pr-14'>
          {/* On phones the tabs get their own row; keep it clear of the
              modal's close button. */}
          <div
            role='tablist'
            aria-label={t('Voices')}
            className='mr-10 flex max-w-full shrink-0 self-start overflow-x-auto rounded-xl bg-gray-100 p-1 dark:bg-white/5 sm:mr-0 sm:self-auto'
          >
            {tabs.map((tab) => {
              const active = scope === tab.key;
              return (
                <button
                  key={tab.key}
                  type='button'
                  role='tab'
                  aria-selected={active}
                  onClick={() => setScope(tab.key)}
                  className={clsxm(
                    'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[13px] font-medium transition',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                    active
                      ? 'bg-white text-gray-900 shadow-sm dark:bg-slate-700 dark:text-white'
                      : 'text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white',
                  )}
                >
                  {tab.label}
                  {Boolean(tab.count) && (
                    <span
                      className={clsxm(
                        'text-[11px] tabular-nums',
                        active
                          ? 'text-gray-500 dark:text-slate-300'
                          : 'text-gray-400 dark:text-slate-500',
                      )}
                    >
                      {formatCount(tab.count || 0)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className='flex items-center gap-2 sm:ml-auto'>
            <div className='relative min-w-0 flex-1 sm:w-72 sm:flex-none'>
              <SearchIcon className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
              <input
                ref={searchRef}
                type='text'
                value={filters.search}
                onChange={(event) =>
                  updateFilters({ search: event.target.value })
                }
                placeholder={t('Search by name, accent or style...')}
                aria-label={t('Search voices')}
                className='h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-8 text-sm text-gray-900 placeholder-gray-400 transition focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-gray-900/5 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder-slate-500 dark:focus:border-white/20 dark:focus:bg-white/10'
              />
              {hasSearch && (
                <button
                  type='button'
                  onClick={() => {
                    updateFilters({ search: '' });
                    searchRef.current?.focus();
                  }}
                  aria-label={t('Clear search')}
                  className='absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:text-gray-700 dark:hover:text-white'
                >
                  <XIcon className='h-4 w-4' />
                </button>
              )}
            </div>
            <button
              type='button'
              onClick={() => setShowMobileFilters(true)}
              className={clsxm(
                'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-xl border px-3 text-[13px] font-medium md:hidden',
                activeFilters > 0
                  ? 'border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-gray-900'
                  : 'border-gray-200 text-gray-700 dark:border-white/10 dark:text-slate-300',
              )}
            >
              <AdjustmentsIcon className='h-4 w-4' />
              {t('Filters')}
              {activeFilters > 0 && ` · ${activeFilters}`}
            </button>
          </div>
        </div>

        <div className='flex min-h-[2.75rem] flex-wrap items-center gap-1.5 px-4 py-2 sm:px-5'>
          <span
            aria-live='polite'
            className='mr-1 text-[13px] font-medium text-gray-900 dark:text-slate-100'
          >
            {isLoading ? t('Loading voices...') : resultsLabel}
          </span>
          {activeChips.map((chip) => (
            <button
              key={chip.key}
              type='button'
              onClick={chip.clear}
              aria-label={`${t('Remove filter')}: ${chip.label}`}
              className='inline-flex h-7 items-center gap-1 rounded-full bg-gray-100 pl-2.5 pr-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-200 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/15'
            >
              {chip.label}
              <XIcon className='h-3.5 w-3.5 text-gray-400' />
            </button>
          ))}
          {!noFilters && (
            <button
              type='button'
              onClick={clearFilters}
              className='ml-auto text-xs font-medium text-gray-500 underline-offset-4 hover:text-gray-900 hover:underline dark:text-slate-400 dark:hover:text-white'
            >
              {t('Clear all')}
            </button>
          )}
        </div>

        <div
          ref={listRef}
          className='flex-1 overflow-y-auto overscroll-contain px-2 pb-4 sm:px-3'
        >
          {isLoading ? (
            <ul aria-hidden='true' className='space-y-1'>
              {Array.from({ length: 8 }).map((_, index) => (
                <li
                  key={index}
                  className='flex items-center gap-3 rounded-xl px-3 py-2.5'
                >
                  <span className='h-11 w-11 shrink-0 animate-pulse rounded-full bg-gray-100 dark:bg-white/5' />
                  <span className='flex-1 space-y-2'>
                    <span className='block h-3 w-40 max-w-full animate-pulse rounded bg-gray-100 dark:bg-white/5' />
                    <span className='block h-2.5 w-64 max-w-full animate-pulse rounded bg-gray-100 dark:bg-white/5' />
                  </span>
                  <span className='hidden h-3 w-28 animate-pulse rounded bg-gray-100 dark:bg-white/5 md:block' />
                </li>
              ))}
            </ul>
          ) : filteredVoices.length === 0 ? (
            <div className='flex flex-col items-center justify-center px-6 py-20 text-center'>
              <div className='flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-white/5'>
                {emptyFavourites ? (
                  <HeartIcon className='h-5 w-5' />
                ) : (
                  <SearchIcon className='h-5 w-5' />
                )}
              </div>
              <p className='mt-4 text-sm font-medium text-gray-900 dark:text-slate-100'>
                {emptyFavourites
                  ? t('No favorites yet')
                  : t('No voices match these filters.')}
              </p>
              {emptyFavourites && (
                <p className='mt-1 max-w-xs text-[13px] text-gray-500 dark:text-slate-400'>
                  {t('Tap the heart on a voice to keep it here.')}
                </p>
              )}
              {!noFilters && (
                <button
                  type='button'
                  onClick={clearFilters}
                  className='mt-5 h-9 rounded-xl border border-gray-200 px-4 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5'
                >
                  {t('Clear filters')}
                </button>
              )}
            </div>
          ) : (
            visibleSections.map((section) => (
              <div key={section.key}>
                {section.title && (
                  <h3 className='px-3 pb-1.5 pt-3 text-xs font-medium text-gray-500 dark:text-slate-400'>
                    {section.title}
                  </h3>
                )}
                <ul className='space-y-0.5'>
                  {section.voices.map((voice) => (
                    <VoiceRow
                      key={voice.voice_id}
                      voice={voice}
                      name={voice.name}
                      meta={voiceMeta(voice)}
                      language={voiceLanguage(voice)}
                      badges={voiceBadges(voice)}
                      selected={voice.voice_id === selectedId}
                      playing={playingId === voice.voice_id}
                      favorite={favouriteSet.has(voice.voice_id)}
                      locked={isLocked(voice)}
                      deletable={isDeletableVoice(voice)}
                      reserveDeleteSlot={reserveDeleteSlot}
                      reserveUpgradeSlot={reserveUpgradeSlot}
                      labels={rowLabels}
                      onSelect={() => selectVoice(voice)}
                      onUse={() => chooseVoice(voice)}
                      onTogglePlay={
                        voice.preview_url ? () => togglePlay(voice) : undefined
                      }
                      onToggleFavorite={() => toggleFavourite(voice.voice_id)}
                      onDelete={() => handleDelete(voice)}
                    />
                  ))}
                </ul>
              </div>
            ))
          )}
          {!isLoading && visibleCount < filteredVoices.length && (
            <div
              ref={sentinelRef}
              aria-hidden='true'
              className='flex justify-center py-6'
            >
              <span className='h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-gray-500 dark:border-white/10 dark:border-t-white/60' />
            </div>
          )}
        </div>

        <div className='flex items-center gap-3 border-t border-gray-100 px-4 py-3 dark:border-white/5 sm:px-5'>
          {selectedVoice ? (
            <div className='flex min-w-0 items-center gap-2.5'>
              <VoiceAvatar voice={selectedVoice} size='sm' />
              <div className='min-w-0'>
                <p className='truncate text-sm font-medium text-gray-900 dark:text-white'>
                  {selectedVoice.name}
                </p>
                <p className='hidden truncate text-xs text-gray-500 dark:text-slate-400 sm:block'>
                  {[
                    voiceLanguage(selectedVoice)?.label,
                    voiceMeta(selectedVoice),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </div>
          ) : (
            <p className='text-sm text-gray-500 dark:text-slate-400'>
              {t('No voice selected')}
            </p>
          )}
          <div className='ml-auto flex shrink-0 items-center gap-2'>
            <button
              type='button'
              onClick={cancel}
              className='h-9 rounded-xl px-4 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-white/10'
            >
              {t('Cancel')}
            </button>
            <button
              type='button'
              onClick={closeModal}
              className='h-9 rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900'
            >
              {t('Select Voice')}
            </button>
          </div>
        </div>
      </section>

      {showMobileFilters && (
        <div className='absolute inset-0 z-40 flex flex-col bg-white dark:bg-slate-900 md:hidden'>
          <div className='flex items-center gap-2 border-b border-gray-100 py-3 pl-2 pr-14 dark:border-white/5'>
            {/* The X in the corner closes the whole picker; this goes back. */}
            <button
              type='button'
              onClick={() => setShowMobileFilters(false)}
              aria-label={t('Show results')}
              className='rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white'
            >
              <ChevronLeftIcon className='h-5 w-5' />
            </button>
            <h3 className='text-[15px] font-semibold text-gray-900 dark:text-white'>
              {t('Filters')}
            </h3>
            {activeFilters > 0 && (
              <button
                type='button'
                onClick={clearFilters}
                className='text-xs font-medium text-gray-500 underline underline-offset-4 dark:text-slate-400'
              >
                {t('Clear all')}
              </button>
            )}
          </div>
          <div className='flex-1 overflow-y-auto px-4 py-5'>{filterPanel}</div>
          <div className='border-t border-gray-100 px-4 py-3 dark:border-white/5'>
            <button
              type='button'
              onClick={() => setShowMobileFilters(false)}
              className='h-10 w-full rounded-xl bg-gray-900 text-sm font-semibold text-white dark:bg-white dark:text-gray-900'
            >
              {t('Show results')} · {resultsLabel}
            </button>
          </div>
        </div>
      )}

      <audio ref={audioRef} onEnded={() => setPlayingId(null)} />
    </div>
  );
};

export default VoiceSelection;
